import { useCallback } from 'react';

import {
  useIsVolopSearchDeduplicateEnabled,
  useSearchGetters,
} from '#/modules/search/contexts/SearchContext';
import { extractLocationAnalyticsProperties } from '#/modules/search/helpers/analytics/extractLocationAnalyticsProperties';
import { searchGetFiltersForAnalytics } from '#/modules/search/helpers/analytics/searchGetFiltersForAnalytics';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';

type Args = {
  searchFrom?: string;
  searchFromPageType?: SearchFromPageType;
};

export function useSearchGetTrackingData() {
  const {
    getSearchCurrentPageResults,
    getSearchFiltersById,
    getSearchFilterType,
    getSearchLocation,
    getSearchPageIndex,
    getSearchQuery,
    getSearchRadius,
    getSearchPromotedListing,
    getVolopListingTotal,
  } = useSearchGetters();

  // Must match the header's listing total when dedupe+VOLOP is active
  const isVolopSearchDeduplicateEnabled = useIsVolopSearchDeduplicateEnabled();

  const searchGetTrackingData = useCallback(
    ({ searchFrom, searchFromPageType }: Args) => {
      const searchLocation = getSearchLocation();

      if (searchLocation) {
        const results = getSearchCurrentPageResults() ?? {
          nbHits: 0,
          nbPages: 0,
        };

        const filters = getSearchFiltersById();

        // Under dedupe+VOLOP the raw nbHits is a deduped org count; use the
        // true listing total so analytics matches what the header displays.
        const volopListingTotal = getVolopListingTotal();
        const isDedupedVolop =
          isVolopSearchDeduplicateEnabled && getSearchFilterType() === 'VOLOP';

        return {
          current_page: getSearchPageIndex() + 1,
          geo_location_enabled: Boolean(searchLocation?.latitude),
          ...searchGetFiltersForAnalytics({ filtersById: filters }),
          search_query: getSearchQuery(),
          search_radius: getSearchRadius(),
          number_of_results:
            isDedupedVolop && volopListingTotal != null
              ? volopListingTotal
              : results.nbHits,
          number_of_pages: results.nbPages,
          search_from: searchFrom,
          search_from_page_type: searchFromPageType,
          promoted_listing_id:
            getSearchPromotedListing()?.volopHit.objectID || null,
          ...extractLocationAnalyticsProperties(searchLocation),
        };
      }

      return {};
    },
    [
      getSearchCurrentPageResults,
      getSearchFiltersById,
      getSearchFilterType,
      getSearchLocation,
      getSearchPageIndex,
      getSearchQuery,
      getSearchRadius,
      getSearchPromotedListing,
      getVolopListingTotal,
      isVolopSearchDeduplicateEnabled,
    ],
  );

  return { searchGetTrackingData };
}
